/**
 * Fast Bundle <-> Horizon cart drawer bridge.
 *
 * Fast Bundle opens the drawer by calling the element directly:
 *
 *   openCartDrawer = () => getDetectedCartDrawers().forEach(d => d.open?.call(d))
 *
 * which reaches cart-drawer-component.open() -> showDialog(). Nothing in that
 * path re-renders the cart, so the drawer shows whatever markup the page was
 * served with — an empty cart, on a first add.
 *
 * The app does have a refresh path, but it is off by default:
 *
 *   if (!getConfig().isSyncAfterDiscount) { ...; return }   // default false
 *   syncCartDrawer()                                       // dispatches cart:update
 *
 * and even when enabled it dispatches `cart:update` with an empty `detail.data`,
 * which Horizon can only act on with an async section fetch — while the same
 * event has already auto-opened the drawer (CartAddEvent.eventName ===
 * ThemeEvents.cartUpdate, see assets/events.js). So both paths are broken.
 *
 * This bridge:
 *
 *   1. Wraps fetch to notice cart mutations (/cart/add.js and friends) and marks
 *      the rendered cart stale, then refreshes it eagerly.
 *   2. Wraps the drawer's open()/showDialog() so an open that lands while a
 *      refresh is in flight waits for it, instead of painting stale markup.
 *   3. Still intercepts the app's `cart:update` if a merchant turns
 *      is_sync_after_discount on, so that path stops racing too.
 *
 * A refresh re-dispatches `cart:update` carrying `detail.data.sections`, which
 * is the shape cart-items-component morphs from synchronously, and `itemCount`,
 * which is what cart-icon.js reads for the bubble.
 *
 * Loaded as a classic, non-deferred head script for two reasons: its fetch
 * wrapper has to be installed before the Fast Bundle SDK captures window.fetch,
 * and its cart:update listener has to be registered before the theme's modules
 * upgrade cart-drawer-component (for an event dispatched on `document`, every
 * listener on `document` runs in registration order — `capture` buys no
 * priority).
 *
 * Set localStorage `fastBundleDebug` to '1' to log the sequence.
 */
(function () {
  'use strict';

  if (window.__fastBundleCartBridge) return;
  window.__fastBundleCartBridge = true;

  var CART_UPDATE = 'cart:update';
  var APP_SOURCE_ID = 'console-force-update';
  var BRIDGE_SOURCE_ID = 'fast-bundle-bridge';
  var MUTATION_PATHS = ['/cart/add', '/cart/change', '/cart/update', '/cart/clear'];
  /** header the SDK stamps on its own cart requests */
  var APP_HEADER = 'x-fastbundle-request';
  /** how long to wait for the server-side bundle discount before rendering anyway */
  var DISCOUNT_TIMEOUT_MS = 4000;
  var DISCOUNT_POLL_MS = 250;

  /** in-flight refresh, or null */
  var pending = null;
  /** a mutation landed after the current refresh started */
  var restale = false;

  log('installed');

  /* ---------------------------------------------------------------------------
     1. Notice cart mutations
  --------------------------------------------------------------------------- */

  var nativeFetch = window.fetch.bind(window);

  window.fetch = function (input, init) {
    var result = nativeFetch(input, init);

    if (isCartMutation(input) && !rendersItsOwnResult(init)) {
      result.then(
        function (response) {
          if (response && response.ok) {
            log('cart mutation', urlOf(input));
            refresh();
          }
        },
        function () {}
      );
    }

    return result;
  };

  function isCartMutation(input) {
    var url = urlOf(input);
    if (!url) return false;

    for (var i = 0; i < MUTATION_PATHS.length; i++) {
      if (url.indexOf(MUTATION_PATHS[i]) !== -1) return true;
    }
    return false;
  }

  /**
   * A caller that asks for `sections` in a cart request is going to render the
   * response itself — that is how the theme handles quantity changes, removals
   * and its own product forms. Refreshing on top of those renders the drawer a
   * second time, which reads as a flash of everything reloading. Fast Bundle's
   * own add asks for no sections, so it still gets picked up. Its requests also
   * carry an identifying header, which is checked first.
   *
   * @param {RequestInit | undefined} init
   */
  function rendersItsOwnResult(init) {
    if (!init) return false;
    if (hasAppHeader(init.headers)) return false;

    return typeof init.body === 'string' && init.body.indexOf('"sections"') !== -1;
  }

  /**
   * @param {HeadersInit | undefined} headers
   */
  function hasAppHeader(headers) {
    if (!headers) return false;

    try {
      if (typeof Headers !== 'undefined' && headers instanceof Headers) return headers.has(APP_HEADER);

      if (Array.isArray(headers)) {
        return headers.some(function (entry) {
          return entry && String(entry[0]).toLowerCase() === APP_HEADER;
        });
      }

      return Object.keys(headers).some(function (key) {
        return key.toLowerCase() === APP_HEADER;
      });
    } catch (error) {
      return false;
    }
  }

  function urlOf(input) {
    try {
      if (typeof input === 'string') return input;
      if (typeof Request !== 'undefined' && input instanceof Request) return input.url;
      if (typeof URL !== 'undefined' && input instanceof URL) return input.href;
    } catch (error) {
      /* fall through */
    }
    return '';
  }

  /* ---------------------------------------------------------------------------
     2. Hold the drawer's open until the refresh lands
  --------------------------------------------------------------------------- */

  if (window.customElements) {
    customElements.whenDefined('cart-drawer-component').then(function () {
      var constructor = customElements.get('cart-drawer-component');
      if (!constructor) return;

      var proto = constructor.prototype;

      /*
        open() calls this.showDialog() internally, and the auto-open handler
        calls showDialog() directly, so both need wrapping. Assigning
        showDialog here shadows the inherited DialogComponent method.
      */
      ['open', 'showDialog'].forEach(function (name) {
        var original = proto[name];
        if (typeof original !== 'function') return;

        proto[name] = function () {
          var element = this;
          var args = arguments;

          if (!pending) return original.apply(element, args);

          log('open deferred until refresh completes:', name);
          pending.then(function () {
            original.apply(element, args);
          });
        };
      });

      log('drawer patched');
    });
  }

  /* ---------------------------------------------------------------------------
     3. Intercept the app's own sync event, if it is ever enabled
  --------------------------------------------------------------------------- */

  document.addEventListener(
    CART_UPDATE,
    function (event) {
      var detail = event.detail;

      if (!detail || detail.sourceId !== APP_SOURCE_ID) return;
      if (detail.data && detail.data.sections) return;

      /* withhold it — nothing has reacted yet, so no stale open, no stale fetch */
      event.stopImmediatePropagation();
      log('app sync event intercepted');
      refresh();
    },
    true
  );

  /* ---------------------------------------------------------------------------
     Refresh
  --------------------------------------------------------------------------- */

  function refresh() {
    if (pending) {
      restale = true;
      return pending;
    }

    pending = load()
      .then(function (result) {
        publish(result.cart, result.sections);
      })
      .catch(function (error) {
        console.error('[fast-bundle] cart refresh failed', error);
      })
      .then(function () {
        pending = null;
        if (restale) {
          restale = false;
          refresh();
        }
      });

    return pending;
  }

  /**
   * The cart has to settle before anything is rendered from it. Fast Bundle
   * applies the bundle discount server-side, after the add, and on this code
   * path it does not wait for it:
   *
   *   async function waitForDiscountApplication() {
   *     return new Promise(e => setTimeout(e, 1))
   *   }
   *
   * so the cart is briefly at full price. Its other code path polls /cart.js
   * until every bundle line carries a discount, which is what this mirrors —
   * otherwise the drawer renders undiscounted prices and only corrects itself
   * on the next cart interaction.
   */
  function load() {
    return waitForDiscount().then(function (cart) {
      var sectionId = getCartSectionId();

      /* the section has to be rendered after the discount lands, not alongside */
      if (!sectionId) return { cart: cart, sections: {} };

      return getSectionHTML(sectionId).then(function (html) {
        var sections = {};
        if (html) sections[sectionId] = html;

        return { cart: cart, sections: sections };
      });
    });
  }

  /**
   * Polls until the cart shows a discount, or the budget runs out. Resolves with
   * the last cart read either way — a bundle can legitimately carry no discount,
   * and that case must not block the drawer forever.
   *
   * @returns {Promise<object>}
   */
  function waitForDiscount() {
    var deadline = Date.now() + DISCOUNT_TIMEOUT_MS;

    function attempt() {
      return getCart().then(function (cart) {
        if (!cart || !cart.item_count) return cart;
        if (hasDiscount(cart)) {
          log('discount applied');
          return cart;
        }

        if (Date.now() >= deadline) {
          log('discount wait timed out, rendering as-is');
          return cart;
        }

        return wait(DISCOUNT_POLL_MS).then(attempt);
      });
    }

    return attempt();
  }

  /**
   * Mirrors the app's own test where possible: it looks for a line-level
   * discount whose title starts with the configured code prefix. Falls back to
   * any discount at all when the prefix isn't exposed.
   *
   * @param {object} cart
   */
  function hasDiscount(cart) {
    if (cart.total_discount > 0) return true;
    if (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length) return true;

    var prefix = discountPrefix();
    var items = cart.items || [];

    for (var i = 0; i < items.length; i++) {
      var discounts = items[i].discounts || [];
      if (!discounts.length) continue;
      if (!prefix) return true;

      for (var j = 0; j < discounts.length; j++) {
        if (String(discounts[j].title || '').indexOf(prefix) !== -1) return true;
      }
    }

    return false;
  }

  function discountPrefix() {
    try {
      return window.FastBundleConf.bundleBox.discount_code_prefix || '';
    } catch (error) {
      return '';
    }
  }

  function wait(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  /**
   * The drawer's cart-items-component carries the id of the section it lives in.
   * @returns {string | null}
   */
  function getCartSectionId() {
    var component = document.querySelector('cart-items-component[data-section-id]');
    return component ? component.getAttribute('data-section-id') : null;
  }

  function getCart() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    return nativeFetch(root + 'cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    }).then(function (response) {
      if (!response.ok) throw new Error('cart.js responded ' + response.status);
      return response.json();
    });
  }

  /**
   * Built the same way as the theme's section-renderer, so both share a cache key.
   * @param {string} sectionId
   */
  function getSectionHTML(sectionId) {
    var url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.sort();

    return nativeFetch(url.toString(), { cache: 'no-store' }).then(function (response) {
      if (!response.ok) throw new Error('section render responded ' + response.status);
      return response.text();
    });
  }

  /**
   * Hands the new markup to the theme. Auto-open is withheld for the duration of
   * the dispatch: the drawer treats every `cart:update` as an add and would pop
   * itself open on a refresh that was never meant to open it. Opening stays the
   * job of whoever called open() — the app, or the header button.
   *
   * @param {object} cart
   * @param {Record<string, string>} sections
   */
  function publish(cart, sections) {
    var suppressed = document.querySelectorAll('cart-drawer-component[auto-open]');

    for (var i = 0; i < suppressed.length; i++) suppressed[i].removeAttribute('auto-open');

    try {
      document.dispatchEvent(
        new CustomEvent(CART_UPDATE, {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: BRIDGE_SOURCE_ID,
            data: {
              sections: sections,
              itemCount: cart && typeof cart.item_count === 'number' ? cart.item_count : 0,
              source: BRIDGE_SOURCE_ID,
            },
          },
        })
      );
      log('published', cart && cart.item_count, 'items');
    } finally {
      /* listeners are synchronous, so the attribute can go straight back on */
      for (var j = 0; j < suppressed.length; j++) suppressed[j].setAttribute('auto-open', '');
    }
  }

  function log() {
    try {
      if (localStorage.getItem('fastBundleDebug') !== '1') return;
    } catch (error) {
      return;
    }

    console.log.apply(console, ['[fast-bundle]'].concat(Array.prototype.slice.call(arguments)));
  }
})();
